import { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { toast } from 'sonner';
import { useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import useChatState from "../components/useChatAssist";


const AgentAssist = () => {
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [queryLevel, setQueryLevel] = useState("😀"); // Default smiley
  const [activeTab, setActiveTab] = useState("chat"); // Default active tab
  const [caseNumber, setCaseNumber] = useState(""); // Case number input
  const [activeStep, setActiveStep] = useState(1); // Current active step in the workflow
  const [isLoaded, setIsLoaded] = useState(false); // Animation state

  // Get authentication context
  const { user, logout } = useContext(AuthContext);

  // Use our custom hook for persistent chat state
  const {
    chatHistory,
    setChatHistory,
    savedChats,
    setSavedChats,
    chatInput,
    setChatInput,
    processingMessage,
    setProcessingMessage,
    chatConnection,
    setChatConnection,
    activeChatId,
    setActiveChatId,
    saveChatModalOpen,
    setSaveChatModalOpen,
    chatTitle,
    setChatTitle
  } = useChatState();

  // Ref to track if component is mounted
  const isMounted = useRef(true);

  // Get current location to detect route changes
  const location = useLocation();

  // Reset and replay animations on component mount and when route changes
  useEffect(() => {
    // Reset animation state first
    setIsLoaded(false);

    // Small delay to ensure reset takes effect before starting animations
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);

    return () => clearTimeout(timer);
  }, [location.pathname]); // Re-run effect when the path changes

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Function to determine query complexity
  const analyzeQueryLevel = (input) => {
    const length = input.length;
    const wordCount = input.split(" ").length;

    if (length < 30 && wordCount < 5) return "😀"; // Simple
    if (length < 80 && wordCount < 15) return "😐"; // Moderate
    return "😞"; // Complex
  };

  // Handle input change & update gauge
  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setQueryLevel(analyzeQueryLevel(newQuery));
  };

  // Handle chat input change
  const handleChatInputChange = (e) => {
    setChatInput(e.target.value);
  };

  // Handle case number change
  const handleCaseNumberChange = (e) => {
    setCaseNumber(e.target.value);
  };

  // Handle search API call
  const handleSearch = async () => {
    if (!query.trim()) {
      alert("Please enter a question.");
      return;
    }

    setLoading(true);
    setResponse("");

    const userMessage = {
      sender: "user",
      text: query,
      timestamp: new Date().toISOString(),
    };

    setChatHistory(prev => [...prev, userMessage]);

    try {
      const res = await axios.post("http://localhost:5000/api/rag-search", { query });

      if (isMounted.current) {  
        setResponse(res.data.response);

        const botResponse = {
          sender: "bot",
          text: res.data.response || "I couldn't process that. Please try again.",
          timestamp: new Date().toISOString(),
        };

        setChatHistory(prev => [...prev, botResponse]);
      }
    } catch (error) {
      console.error("Error:", error);

      if (isMounted.current) {
        const errorMessage = {
          sender: "system",
          text: "❌ Error fetching response.",
          timestamp: new Date().toISOString(),
        };

        setChatHistory(prev => [...prev, errorMessage]);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };


  // Check network connection status periodically
  useEffect(() => {
    const checkNetworkStatus = () => {
      setChatConnection(navigator.onLine);
    };

    // Initial check
    checkNetworkStatus();

    // Listen for online/offline events
    window.addEventListener('online', checkNetworkStatus);
    window.addEventListener('offline', checkNetworkStatus);

    // Cleanup
    return () => {
      window.removeEventListener('online', checkNetworkStatus);
      window.removeEventListener('offline', checkNetworkStatus);
    };
  }, []);

  // Handle chat messages
  const handleChatSubmit = async () => {
    if (!chatInput.trim() || processingMessage) return;

    const userMessage = {
      sender: "user",
      text: chatInput,
      timestamp: new Date().toISOString(),
    };

    // Store a local copy of the updated history
    const updatedHistory = [...chatHistory, userMessage];

    // Update state immediately with user message
    setChatHistory(updatedHistory);
    setChatInput("");
    setProcessingMessage(true);

    if (!chatConnection) {
      const systemMessage = {
        sender: "system",
        text: "No internet connection. Your message will be sent when connection is restored.",
        timestamp: new Date().toISOString(),
      };
      setChatHistory([...updatedHistory, systemMessage]);
      setProcessingMessage(false);
      return;
    }

    try {
      console.log("Sending request with query:", chatInput);
      const res = await axios.post("http://localhost:5000/api/rag-search", {
        query: chatInput,
      });

      console.log("Complete response:", res);
      console.log("Response data:", res.data);

      // Simplify response extraction
      let responseText;
      if (res.data && res.data.response) {
        responseText = res.data.response;
        console.log("Extracted response text:", responseText);
      } else {
        responseText = "I couldn't process that. Please try again.";
        console.log("Using default response text");
      }

      const botResponse = {
        sender: "bot",
        text: responseText,
        timestamp: new Date().toISOString(),
      };

      console.log("Bot response to add:", botResponse);

      // Create a new array instead of modifying the existing one
      const finalHistory = [...updatedHistory, botResponse];
      console.log("Final history to set:", finalHistory);

      // Set the new history
      setChatHistory(finalHistory);
    } catch (error) {
      console.error("❌ Error details:", error);

      if (error.response) {
        console.error("❌ Error response:", error.response.data);
        console.error("❌ Error status:", error.response.status);
      }

      const errorMessage = {
        sender: "system",
        text: `Error: ${error.response?.data?.message || error.message || "Unknown error occurred"}`,
        timestamp: new Date().toISOString(),
      };

      setChatHistory([...updatedHistory, errorMessage]);
    } finally {
      setProcessingMessage(false);
    }
  };


  // Start a new chat
  const startNewChat = () => {
    // Only create new chat if we have messages
    if (chatHistory.length > 0) {
      // Make sure we're on "current" before starting a new chat
      setActiveChatId("current");
      setChatHistory([]);
      setChatInput("");

      toast.success('Started a new chat', {
        duration: 2000,
        icon: '✨'
      });
    }
  };

  const loadSavedChat = (chatId) => {
    // Find the chat with this ID
    const chatToLoad = savedChats.find(chat => chat.id === chatId);

    if (chatToLoad) {
      // Set this as the active chat ID
      setActiveChatId(chatId);

      // Load its messages into the chat history
      setChatHistory([...chatToLoad.messages]);

      // Notify the user
      toast.success(`Loaded chat: ${chatToLoad.title}`, {
        duration: 2000,
        icon: '📂'
      });
    }
  };

  // Add function to delete a saved chat
  const deleteSavedChat = (chatId, e) => {
    // Stop event propagation to prevent loading the chat
    e.stopPropagation();

    // Remove this chat from saved chats
    setSavedChats(prev => prev.filter(chat => chat.id !== chatId));

    // If this was the active chat, switch back to current
    if (activeChatId === chatId) {
      setActiveChatId("current");
      setChatHistory([]);
    }

    // Notify the user
    toast.success('Chat deleted', {
      duration: 2000,
      icon: '🗑️'
    });
  };

  // Copy chat to clipboard
  const handleCopyChat = () => {
    const chatText = chatHistory.map(msg => `${msg.sender === 'user' ? 'You' : 'Assistant'}: ${msg.text}`).join('\n');
    navigator.clipboard.writeText(chatText)
      .then(() => {
        toast.success('Chat copied to clipboard!', {
          duration: 2000,
          icon: '📋'
        });
      })
      .catch(err => {
        console.error("Could not copy text: ", err);
        toast.error('Failed to copy chat', {
          description: 'Please try again',
          duration: 3000
        });
      });
  };
  // Updated openSaveChatModal function to pre-fill with existing title
  const openSaveChatModal = () => {
    // If we're editing an existing saved chat, pre-fill the title field
    if (activeChatId !== "current") {
      const currentChat = savedChats.find(chat => chat.id === activeChatId);
      if (currentChat) {
        setChatTitle(currentChat.title);
      } else {
        setChatTitle(""); // Reset if not found
      }
    } else {
      setChatTitle(""); // Reset for new chats
    }

    setSaveChatModalOpen(true);
  };

  // Close save chat modal
  const closeSaveChatModal = () => {
    setSaveChatModalOpen(false);
  };

  // Handle chat title change
  const handleChatTitleChange = (e) => {
    setChatTitle(e.target.value);
  };

  // Improved save chat function that handles saving current chat 
  // or updating an existing saved chat
  const handleSaveChat = () => {
    if (!chatTitle.trim()) {
      toast.error('Please enter a title for your chat', {
        duration: 3000
      });
      return;
    }

    // Get user identifier
    const userId = user?.id || user?._id || user?.username || 'guest';

    // Check if we're updating an existing saved chat or creating a new one
    if (activeChatId !== "current") {
      // We're updating an existing saved chat
      setSavedChats(prev =>
        prev.map(chat =>
          chat.id === activeChatId
            ? {
              ...chat,
              title: chatTitle, // Update title if changed
              messages: [...chatHistory], // Update with current messages
              lastUpdated: new Date().toISOString() // Add last updated timestamp
            }
            : chat
        )
      );

      // Close modal
      setSaveChatModalOpen(false);

      // Start a new chat
      setActiveChatId("current");
      setChatHistory([]);
      setChatInput("");

      // Show success message for update
      toast.success('Chat updated and new chat started!', {
        description: `"${chatTitle}" has been updated with latest messages`,
        duration: 3000,
        icon: '🔄'
      });
    } else {
      // Creating a new saved chat
      const newSavedChat = {
        id: `chat_${Date.now()}`,
        userId: userId,
        title: chatTitle,
        timestamp: new Date().toISOString(),
        messages: [...chatHistory]
      };

      // Add to saved chats
      setSavedChats(prev => [...prev, newSavedChat]);

      // Close modal
      setSaveChatModalOpen(false);

      // Start a new chat
      setActiveChatId("current");
      setChatHistory([]);
      setChatInput("");

      // Show success message for new chat
      toast.success('Chat saved and new chat started!', {
        description: `"${chatTitle}" with ${chatHistory.length} messages has been saved`,
        duration: 3000,
        icon: '💾'
      });
    }
  };

  // Get the status of a step (completed, active, upcoming)
  const getStepStatus = (stepNumber) => {
    if (stepNumber < activeStep) return "completed";
    if (stepNumber === activeStep) return "active";
    return "upcoming";
  };

  // Handle user logout
  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully', {
      duration: 2000,
      icon: '👋'
    });
  };

  // Render user profile component
  const renderUserProfile = () => {
    return (
      <div className="flex items-center">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-200 mr-2">
          {user?.name ? user.name.charAt(0).toUpperCase() :
            user?.username ? user.username.charAt(0).toUpperCase() : 'G'}
        </div>
        <div className="flex flex-col">
          <div className="font-medium text-sm dark:text-white">
            {user?.name || user?.username || 'Guest'}
          </div>
          {user && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {user?.department && `${user.department} • `}{user?.role || 'User'}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Tabbing System */}
      <div className="flex-grow overflow-y-auto px-2 md:px-4 py-3">
        <div className={`bg-white bg-opacity-60 dark:bg-white dark:bg-opacity-10 rounded-lg p-4 h-full flex flex-col transition-all duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
          {/* Tab Buttons and User Profile */}
          <div className={`flex justify-between border-b border-gray-200 dark:border-gray-700 mb-4 transition-all duration-500 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="flex">
                    <button
                      className={`flex items-center px-4 py-2 ${activeTab === 'chat'
                        ? 'border-b-2 border-gray-800 dark:border-uasYellow text-gray-800 dark:text-white font-medium'
                        : 'text-gray-500 dark:text-gray-400'}`}
                      onClick={() => setActiveTab('chat')}
                    >
                      CHAT ASSISTANT
                    </button>

                    <button
                      className={`flex items-center px-4 py-2 ${activeTab === 'email'
                        ? 'border-b-2 border-gray-800 dark:border-uasYellow text-gray-800 dark:text-white font-medium'
                        : 'text-gray-500 dark:text-gray-400'}`}
                      onClick={() => setActiveTab('email')}
                    >
                      EMAIL TRANSACTION HANDLER
                    </button>


            </div>


            {/* User Profile and Logout */}
            <div className="flex items-center space-x-3">
              {renderUserProfile()}
            </div>
          </div>

          {/* Content Container - Full Height */}
          <div className="flex gap-4 flex-1 h-full overflow-hidden">
            {/* Main Panel */}
            {activeTab === 'chat' ? (
              <div className={`w-7/12 bg-white dark:bg-laryzeDarkGray dark:bg-opacity-90 rounded-lg shadow flex flex-col h-full transition-all duration-500 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                {/* Chat Header */}
                <div className="flex justify-between items-center p-4 border-t-8 rounded-t-md border-t-black dark:border-t-uasYellow border-b border-b-gray-200 dark:border-b-gray-700">
                  <div>
                    <h3 className="text-lg font-bold dark:text-white">
                      {activeChatId !== "current"
                        ? savedChats.find(chat => chat.id === activeChatId)?.title || "Chatbot"
                        : "Chatbot"}
                    </h3>
                    {activeChatId !== "current" && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Saved chat • {savedChats.find(chat => chat.id === activeChatId)?.messages.length || 0} messages
                      </div>
                    )}
                  </div>
                  <button
                    className="px-3 py-1.5 text-sm text-white bg-black dark:bg-uasYellow dark:text-black rounded hover:bg-gray-800 dark:hover:bg-yellow-300 flex items-center"
                    onClick={startNewChat}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    New Chat
                  </button>
                </div>
                {/* Chat Messages Area - Flex Grow to fill available space */}
                <div className="p-4 text-sm flex-grow overflow-y-auto">
                  {chatHistory.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                      Start a new conversation
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {chatHistory.map((message, index) => (
                        <div
                          key={index}
                          className={`flex flex-col ${message.sender === 'user' ? 'items-end' : 'items-start'} transition-all duration-300`}
                        >
                          <div className={`text-xs font-semibold mb-1 ${message.sender === 'user'
                            ? 'text-gray-700 dark:text-gray-300'
                            : 'text-yellow-800 dark:text-yellow-400'}`}>
                            {message.sender === 'user' ? 'You' : 'ChatBot'}
                          </div>
                          <div
                            className={`p-3 rounded-lg ${message.sender === 'user'
                              ? 'bg-yellow-100 dark:bg-gray-400 dark:bg-opacity-40 ml-auto text-gray-800 dark:text-gray-200'
                              : message.sender === 'system'
                                ? 'bg-yellow-100 dark:bg-gray-400 dark:bg-opacity-40 mx-auto text-center text-yellow-800 dark:text-yellow-200'
                                : 'bg-gray-100 dark:bg-gray-500 dark:bg-opacity-50 mr-auto text-gray-800 dark:text-gray-200'
                              } max-w-md inline-block`}
                          >
                            {message.sender === 'bot' ? (
                              <div className="whitespace-pre-line">
                                {message.text.split('\n\n').map((paragraph, i) => {
                                  // Process formatting for bold, italic, and headers
                                  const processFormatting = (text) => {
                                    // Process bold text (either **text** or __text__)
                                    let processed = text.replace(/(\*\*|__)(.*?)\1/g, '<strong>$2</strong>');

                                    // Process italic text (either *text* or _text_)
                                    processed = processed.replace(/(\*|_)(.*?)\1/g, '<em>$2</em>');

                                    // Process inline code (`code`)
                                    processed = processed.replace(/`([^`]+)`/g, '<code class="bg-gray-200 dark:bg-gray-700 px-1 rounded text-sm">$1</code>');

                                    // Process headers (# Header)
                                    if (processed.match(/^#{1,6}\s/)) {
                                      const level = processed.match(/^(#{1,6})\s/)[1].length;
                                      const headerText = processed.replace(/^#{1,6}\s/, '');
                                      return `<h${level} class="font-bold text-${level === 1 ? 'xl' : level === 2 ? 'lg' : 'base'} mb-2">${headerText}</h${level}>`;
                                    }

                                    return processed;
                                  };

                                  if (/^\d+\./.test(paragraph.trim())) {
                                    // Numbered list item
                                    const number = paragraph.match(/^\d+/)[0];
                                    const content = processFormatting(paragraph.replace(/^\d+\./, '').trim());
                                    return (
                                      <div key={i} className="mb-3 flex">
                                        <span className="font-bold mr-2 min-w-6 text-right">{number}.</span>
                                        <span dangerouslySetInnerHTML={{ __html: content }}></span>
                                      </div>
                                    );
                                  } else if (/^[-•*]/.test(paragraph.trim())) {
                                    // Bullet point
                                    const content = processFormatting(paragraph.replace(/^[-•*]/, '').trim());
                                    return (
                                      <div key={i} className="mb-3 flex">
                                        <span className="inline-block min-w-5 text-center">•</span>
                                        <span dangerouslySetInnerHTML={{ __html: content }}></span>
                                      </div>
                                    );
                                  } else if (paragraph.trim().startsWith('```')) {
                                    // Code block
                                    const codeContent = paragraph.replace(/```(\w*)?/, '').replace(/```$/, '');
                                    return (
                                      <pre key={i} className="bg-gray-200 dark:bg-gray-700 p-3 rounded mb-3 overflow-x-auto text-sm">
                                        <code>{codeContent}</code>
                                      </pre>
                                    );
                                  } else {
                                    // Regular paragraph with formatting
                                    const content = processFormatting(paragraph);
                                    return <div key={i} className="mb-3" dangerouslySetInnerHTML={{ __html: content }}></div>;
                                  }
                                })}
                              </div>
                            ) : (
                              message.text
                            )}
                          </div>
                        </div>
                      ))}
                      {processingMessage && (
                        <div className="bg-gray-100 dark:bg-gray-800 mr-auto p-2 rounded-lg max-w-fit inline-block">
                          <div className="flex space-x-2">
                            <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce"></div>
                            <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Action buttons */}
                {chatHistory.length > 0 && (
                  <div className="flex space-x-2 px-4">
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:text-white rounded hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                      onClick={handleCopyChat}
                    >
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </button>
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:text-white rounded hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                      onClick={openSaveChatModal}
                    >
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      Save
                    </button>
                  </div>
                )}
                {/* Input Area - Fixed at bottom */}
                <div className={`p-4 mt-auto transition-all duration-500 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <div className="relative">
                    <input
                      type="text"
                      className="block w-full p-3 pr-12 text-sm border border-gray-300 dark:border-gray-600 dark:bg-white dark:bg-opacity-20 dark:text-white dark:placeholder-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500"
                      placeholder="Ask a question"
                      value={chatInput}
                      onChange={handleChatInputChange}
                      onKeyPress={(e) => e.key === 'Enter' && handleChatSubmit()}
                      disabled={processingMessage}
                    />
                    <button
                      className={`absolute inset-y-0 right-0 flex items-center px-3 text-white ${processingMessage
                        ? 'bg-gray-400 dark:bg-gray-600'
                        : 'bg-black dark:bg-uasYellow dark:text-black'} rounded-r-lg`}
                      onClick={handleChatSubmit}
                      disabled={processingMessage}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // Email Transaction Handler with Case Flow UI
              <div className={`w-full bg-white dark:bg-laryzeDarkGray dark:bg-opacity-90 rounded-lg shadow flex flex-col h-full transition-all duration-500 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                {/* Email Handler Header - Yellow Accent Bar */}
                <div className="border-t-8 rounded-t-md border-t-black dark:border-t-uasYellow ">
                  {/* Header Content - Moved inside with padding */}
                  <div className="p-4">
                    <div className={`flex items-center mb-4 transition-all duration-500 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-white mr-2">Case Number:</label>
                      <input
                        type="text"
                        className="bg-gray-50 border border-gray-300 dark:border-gray-600 dark:bg-white dark:bg-opacity-20 dark:text-white text-gray-900 text-sm rounded-lg w-64 p-2"
                        value={caseNumber}
                        onChange={handleCaseNumberChange}
                        placeholder="Enter case number"
                      />
                    </div>
                    {/* Progress Steps */}
                    <div className={`w-full relative pb-12 transition-all duration-500 delay-400 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                      {/* Connecting Line - Single continuous line */}
                      <div className="absolute top-4 left-0 right-0 h-1 bg-gray-600 dark:bg-gray-600 z-0"></div>
                      {/* Step Circles */}
                      <div className="flex justify-between relative">
                        {[1, 2, 3, 4, 5, 6].map((step) => {
                          const status = getStepStatus(step);
                          const isActive = status === 'active';
                          const isCompleted = status === 'completed';
                          return (
                            <div
                              key={step}
                              className={`flex flex-col items-center z-10 transition-all duration-500 delay-${300 + 50 * step} ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                            >
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full ${isActive || isCompleted
                                ? 'bg-uasYellow text-black'
                                : 'bg-gray-600 text-gray-300'
                                }`}>
                                {isCompleted ? (
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                ) : (
                                  step
                                )}
                              </div>
                              <span className={`mt-2 text-xs ${isActive
                                ? 'text-black font-semibold dark:text-white font-medium'
                                : isCompleted
                                  ? 'text-gray-300'
                                  : 'text-gray-500'
                                }`}>
                                Step {step}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Case Handling Content */}
                <div className={`flex-grow p-4 overflow-y-auto transition-all duration-500 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">1. Initiate Case Handling Promptly</h2>
                  <div className="space-y-4 ml-4">
                    <div className={`flex items-start transition-all duration-500 delay-800 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                      <span className="text-gray-700 dark:text-gray-200 mr-2">1.1</span>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <p className="text-gray-700 dark:text-gray-200">Immediately begin working on the case upon receipt.</p>
                          <div className="flex space-x-4">
                            <label className="inline-flex items-center">
                              <input type="radio" name="question1" className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                              <span className="ml-2 text-gray-700 dark:text-gray-200">Yes</span>
                            </label>
                            <label className="inline-flex items-center">
                              <input type="radio" name="question1" className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                              <span className="ml-2 text-gray-700 dark:text-gray-200">No</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={`flex items-start transition-all duration-500 delay-900 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                      <span className="text-gray-700 dark:text-gray-200 mr-2">1.2</span>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <p className="text-gray-700 dark:text-gray-200">Update the case status from Assigned to In Progress.</p>
                          <div className="flex space-x-4">
                            <label className="inline-flex items-center">
                              <input type="radio" name="question2" className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                              <span className="ml-2 text-gray-700 dark:text-gray-200">Yes</span>
                            </label>
                            <label className="inline-flex items-center">
                              <input type="radio" name="question2" className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                              <span className="ml-2 text-gray-700 dark:text-gray-200">No</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Additional steps would be added here in a complete implementation */}
                </div>
                {/* Action buttons at the bottom */}
                <div className={`p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between transition-all duration-500 delay-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <button className="px-4 py-2 bg-gray-200 text-gray-800 rounded dark:bg-gray-700 dark:text-gray-200">
                    Previous
                  </button>
                  <button className="px-4 py-2 bg-uasYellow text-black rounded hover:bg-yellow-400">
                    Next Step
                  </button>
                </div>
              </div>
            )}
            {/* Secondary Panel - Only show in chat mode */}
            {activeTab === 'chat' && (
              <div className={`w-5/12 bg-white dark:bg-laryzeDarkGray dark:bg-opacity-90 rounded-lg shadow p-4 flex flex-col h-full border-t-8 border-t-black dark:border-t-uasYellow transition-all duration-500 delay-400 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
                <div className="flex flex-col h-full">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold dark:text-white">Saved Chats</h3>
                    {savedChats.length > 0 && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {savedChats.filter(chat => chat.userId === (user?.id || user?._id || user?.username || 'guest')).length} saved chat
                        {savedChats.filter(chat => chat.userId === (user?.id || user?._id || user?.username || 'guest')).length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 flex-grow overflow-y-auto">
                    {/* Current Chat Card */}
                    {chatHistory.length > 0 && activeChatId === "current" && (
                      <div
                        className={`p-3 bg-gray-50 dark:bg-gray-500 dark:bg-opacity-40 rounded border-l-4 border-l-black dark:border-l-uasYellow border border-gray-200 dark:border-gray-700 transition-all duration-500 delay-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="font-medium dark:text-white">Current Chat</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                            Active
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {chatHistory.length} message{chatHistory.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    )}

                    {/* Saved Chats - Only show the current user's chats */}
                    {savedChats
                      .filter(chat => chat.userId === (user?.id || user?._id || user?.username || 'guest'))
                      .map((chat) => (
                        <div
                          key={chat.id}
                          onClick={() => loadSavedChat(chat.id)}
                          className={`p-3 ${activeChatId === chat.id ? 'bg-gray-100 dark:bg-gray-600' : 'bg-white dark:bg-gray-800'} dark:bg-opacity-50 rounded border ${activeChatId === chat.id ? 'border-l-4 border-l-black dark:border-l-uasYellow' : ''} border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 delay-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="font-medium dark:text-white truncate max-w-[80%]">{chat.title}</div>
                            <div className="flex space-x-2">
                              {activeChatId === chat.id && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                                  Active
                                </div>
                              )}
                              <button
                                onClick={(e) => deleteSavedChat(chat.id, e)}
                                className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400 mt-1">
                            <div>{chat.messages.length} message{chat.messages.length !== 1 ? 's' : ''}</div>
                            <div className="text-xs">
                              {new Date(chat.timestamp).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      ))}

                    {/* Empty State */}
                    {savedChats.filter(chat => chat.userId === (user?.id || user?._id || user?.username || 'guest')).length === 0 && chatHistory.length === 0 && (
                      <div className={`flex-grow flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 transition-all duration-500 delay-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                        <svg className="w-12 h-12 mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                        <p>No saved chats yet</p>
                        <p className="text-xs mt-1">Start a conversation and click "Save"</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Save Chat Modal */}
        {saveChatModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-300">
            <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-2xl p-6 w-full max-w-md transform transition-all duration-300 scale-100 opacity-100 animate-fade-in-up">
              <h3 className="text-lg font-bold mb-4 dark:text-white flex items-center">
                <svg className="w-5 h-5 mr-2 text-black dark:text-uasYellow" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save Chat
              </h3>
              <div className="mb-5">
                <label htmlFor="chatTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Chat Title
                </label>
                <input
                  type="text"
                  id="chatTitle"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-white dark:bg-opacity-40 dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-uasYellow focus:border-transparent transition-all"
                  placeholder="Enter a title for this chat"
                  value={chatTitle}
                  onChange={handleChatTitleChange}
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                  onClick={closeSaveChatModal}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium text-white bg-black dark:bg-uasYellow dark:text-black rounded-md hover:bg-gray-800 dark:hover:bg-yellow-400 transition-all shadow-sm"
                  onClick={handleSaveChat}
                >
                  Save Chat
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentAssist;